import React, { useState } from "react";
import styled from "styled-components";
import { Button, Box, Typography } from "@mui/material";
import BiometriaIcon from '@mui/icons-material/Face';
import SecurityIcon from '@mui/icons-material/Security';
import useFormStore from "../state/useFormStore";
import { useNavigate } from "react-router-dom";
import { apiService } from "../utils/api";
import withAuthAndData from './withAuthAndData';
import { config } from '../config';

const Container = styled.div`
  width: 100%;
  max-width: 600px;
  margin: auto;
  text-align: center;
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const Subtitle = styled.h2`
  font-size: 14px;
  font-weight: 500;
  color: #666;
  margin-bottom: 8px;
`;

const CardButton = styled(Button)`
  width: 100%;
  padding: 16px !important;
  text-align: left !important;
  margin-bottom: 16px !important;
  border: 2px solid #1e6373 !important;
  background-color: #95d0dd !important;
  color: #000 !important;
  border-radius: 12px !important;
  transition: all 0.2s ease !important;

  &:hover {
    background-color: #5ab1c4 !important;
    border-color: #1e6373 !important;
    opacity: 0.9;
  }

  &:disabled {
    background-color: #ccc !important;
    border-color: #ccc !important;
  }
`;

const IconWrapper = styled.div`
  margin-right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #000;
  border-radius: 50%;
  color: white;
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const TextWrapper = styled.div`
  text-align: left;
`;

const ButtonTitle = styled(Typography)`
  font-size: 16px !important;
  font-weight: 600 !important;
  margin-bottom: 4px !important;
`;

const ButtonSubtitle = styled(Typography)`
  font-size: 12px !important;
  color: #666 !important;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
`;

const InfoIcon = styled.div`
  margin-top: 3px;
  color: #95d0dd;
`;

const SkipLink = styled.button`
  background: none;
  border: none;
  color: #95d0dd;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-top: 1rem;
  font-family: "Figtree", serif;

  &:hover {
    color: #7ab8c5;
  }

  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

const SkipText = styled.p`
  font-size: 12px;
  color: #666;
  margin-top: 0.5rem;
  text-align: center;
  max-width: 400px;
  line-height: 1.4;
`;

const Biometria: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();

    const hash = useFormStore((state) => state.hash) || localStorage.getItem("hash");
    const { requestUid, first_name } = useFormStore(state => ({
        requestUid: state.requestUid,
        first_name: state.first_name
    }));

    const iniciarBiometria = async () => {
        if (!requestUid) {
            return;
        }

        setLoading(true);

        try {
            const response = await apiService.requests.startIdentityVerification(requestUid);

            if (response.data?.data?.verificationUrl) {
                console.log("URL de verificación obtenida:", response.data.data.verificationUrl);
                window.location.href = response.data.data.verificationUrl;
            } else {
                throw new Error("No se pudo generar la URL de verificación");
            }
        } catch (error) {
            console.error("Error en verificación de identidad:", error);
            alert("Hubo un problema con la verificación de identidad.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col items-center pt-10">
            <Subtitle>
                {first_name ? `${first_name}, p` : 'P'}ara continuar, necesitamos verificar que sos vos
            </Subtitle>

            <Box mt={5} mb={5}>
                <InfoItem>
                    <InfoIcon><SecurityIcon /></InfoIcon>
                    <Box>
                        <Typography fontWeight={600}>Proceso seguro</Typography>
                        <Typography variant="body2" mt={1}>
                            Tus datos están protegidos con los más altos estándares de seguridad.
                        </Typography>
                    </Box>
                </InfoItem>

                <InfoItem>
                    <InfoIcon><BiometriaIcon /></InfoIcon>
                    <Box>
                        <Typography fontWeight={600}>Validación rápida</Typography>
                        <Typography variant="body2" mt={1}>
                            El proceso es 100% online y solo te va a llevar unos minutos.
                        </Typography>
                    </Box>
                </InfoItem>
            </Box>

            <CardButton 
                variant="outlined" 
                onClick={iniciarBiometria}
                disabled={loading}
            >
                <ButtonContent>
                    <IconWrapper>
                        <BiometriaIcon />
                    </IconWrapper>
                    <TextWrapper>
                        <ButtonTitle>{loading ? "Procesando..." : "Validar Identidad"}</ButtonTitle>
                        <ButtonSubtitle>Forma segura y rápida</ButtonSubtitle>
                    </TextWrapper>
                </ButtonContent>
            </CardButton>

            {config && config.environment.biometriaEnabled === false && (
                <>
                    <SkipLink
                    type="button"
                    onClick={() => navigate(`/${hash}/validacion-exitosa`)}
                    disabled={loading}
                    >
                    No puedo validar mi identidad
                    </SkipLink>
                    <SkipText>
                    Podés continuar y un analista te ayudará a validar tu identidad más tarde.
                    </SkipText>
                </>
            )}
        </div>
    );
};

export default withAuthAndData(Biometria);